import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import { apiPrivate } from "@/lib/api";
import { Calendar, MapPin, Video, Loader2, ArrowLeft, Download, CheckCircle2, XCircle, UserCheck, ListChecks, Clock } from "lucide-react";
import { toast } from "sonner";

const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("it-IT", { dateStyle: "full", timeStyle: "short" }); }
    catch { return iso; }
};

export default function Assemblee() {
    const { id } = useParams();
    return id ? <AssemblyDetail id={id} /> : <AssembliesList />;
}

function AssembliesList() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const reload = () => {
        setLoading(true);
        apiPrivate.get("/assemblies")
            .then(r => setItems(r.data || []))
            .finally(() => setLoading(false));
    };
    useEffect(() => { reload(); }, []);

    const active = items.filter(a => a.status !== "closed");
    const closed = items.filter(a => a.status === "closed");

    return (
        <AppLayout>
            <div className="max-w-5xl mx-auto" data-testid="assemblies-list">
                <span className="label-tag text-magenta">Assemblee</span>
                <h1 className="font-display text-5xl mt-3 tracking-tighter">
                    Le tue <span className="font-serif-italic text-gradient-tropical">convocazioni</span>.
                </h1>
                <p className="text-zinc-400 mt-3 max-w-2xl">
                    Conferma la presenza, delega un altro socio o segnati assente. La tua scelta resta modificabile fino alla chiusura dell'assemblea.
                </p>

                {loading ? (
                    <div className="bento p-12 text-center text-zinc-500 mt-8"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
                ) : items.length === 0 ? (
                    <div className="bento p-12 text-center text-zinc-500 mt-8" data-testid="assemblies-empty">
                        <Calendar className="h-10 w-10 mx-auto mb-3 opacity-40" />
                        Nessuna assemblea convocata al momento.
                    </div>
                ) : (
                    <>
                        {active.length > 0 && (
                            <div className="mt-8" data-testid="assemblies-active">
                                <h2 className="text-lg font-display text-zinc-300 mb-3 flex items-center gap-2">
                                    <span className="h-2 w-2 rounded-full bg-magenta animate-pulse" /> Aperte
                                </h2>
                                <div className="space-y-3">
                                    {active.map(a => <ActiveAssemblyRow key={a.id} assembly={a} onChange={reload} />)}
                                </div>
                            </div>
                        )}

                        {closed.length > 0 && (
                            <div className="mt-10 pt-6 border-t border-zinc-900" data-testid="assemblies-closed">
                                <h3 className="text-lg font-display tracking-tighter text-zinc-400">Storico</h3>
                                <div className="space-y-3 mt-4">
                                    {closed.map(a => (
                                        <button
                                            key={a.id}
                                            onClick={() => navigate(`/app/assemblee/${a.id}`)}
                                            className="bento p-5 text-left w-full hover:border-magenta/40 transition"
                                            data-testid={`assembly-card-${a.id}`}
                                        >
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="px-2 py-0.5 rounded-full text-xs bg-zinc-700/40 text-zinc-400">Chiusa</span>
                                                <span className="px-2 py-0.5 rounded-full text-xs bg-magenta/15 text-magenta">{a.type}</span>
                                                <span className="text-xs text-zinc-500">{fmtDate(a.date_time)}</span>
                                            </div>
                                            <h3 className="text-xl font-display tracking-tighter mt-2">{a.title}</h3>
                                            <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500 flex-wrap">
                                                {a.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {a.location}</span>}
                                                {a.videocall_url && <span className="flex items-center gap-1"><Video className="h-3 w-3" /> Da remoto</span>}
                                                {a.agenda?.length > 0 && <span className="flex items-center gap-1"><ListChecks className="h-3 w-3" /> {a.agenda.length} punti</span>}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </AppLayout>
    );
}

function ActiveAssemblyRow({ assembly, onChange }) {
    const navigate = useNavigate();
    const [myRsvp, setMyRsvp] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        apiPrivate.get(`/assemblies/${assembly.id}/my-rsvp`).then(r => setMyRsvp(r.data)).catch(() => { });
    }, [assembly.id]);

    // Deadline effettiva: se non definita, coincide con data assemblea
    const deadlineIso = assembly.rsvp_deadline || assembly.date_time;
    const deadlineDate = deadlineIso ? new Date(deadlineIso) : null;
    const isExpired = deadlineDate ? Date.now() > deadlineDate.getTime() : false;
    const isCustomDeadline = !!assembly.rsvp_deadline;

    const quickRsvp = async (status, e) => {
        e?.stopPropagation();
        if (isExpired) return;
        if (status === "delegato") {
            navigate(`/app/assemblee/${assembly.id}`);
            return;
        }
        setSaving(true);
        try {
            const r = await apiPrivate.put(`/assemblies/${assembly.id}/rsvp`, { status });
            setMyRsvp(r.data);
            toast.success("Risposta salvata");
            onChange?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Errore");
        } finally { setSaving(false); }
    };

    const goDetail = () => navigate(`/app/assemblee/${assembly.id}`);

    return (
        <div className="bento p-5 border-l-4 border-l-magenta" data-testid={`assembly-active-${assembly.id}`}>
            <div onClick={goDetail} className="cursor-pointer" data-testid={`assembly-card-${assembly.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-bayside/15 text-bayside">Aperta</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-magenta/15 text-magenta">{assembly.type}</span>
                    <span className="text-xs text-zinc-500">{fmtDate(assembly.date_time)}</span>
                </div>
                <h3 className="text-xl font-display tracking-tighter mt-2 hover:text-magenta transition">{assembly.title}</h3>
                <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500 flex-wrap">
                    {assembly.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {assembly.location}</span>}
                    {assembly.videocall_url && <span className="flex items-center gap-1"><Video className="h-3 w-3" /> Da remoto</span>}
                    {assembly.agenda?.length > 0 && <span className="flex items-center gap-1"><ListChecks className="h-3 w-3" /> {assembly.agenda.length} punti</span>}
                </div>
            </div>

            {/* RSVP inline nella lista */}
            <div className="mt-4 pt-4 border-t border-zinc-800">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-grotesk">La tua risposta</div>
                    {deadlineDate && (
                        <div
                            className={`text-[10px] font-grotesk flex items-center gap-1 ${isExpired ? "text-vermillion" : "text-zinc-500"}`}
                            data-testid={`rsvp-deadline-${assembly.id}`}
                        >
                            <Clock className="h-3 w-3" />
                            {isExpired
                                ? "Termine scaduto"
                                : (isCustomDeadline
                                    ? <>Rispondi entro <span className="text-white ml-1">{fmtDate(deadlineIso)}</span></>
                                    : <>Rispondi entro il giorno dell'assemblea</>
                                )}
                        </div>
                    )}
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button
                        onClick={(e) => quickRsvp("presente", e)}
                        disabled={saving || isExpired}
                        className={`px-4 py-2 rounded-full text-sm border-2 transition ${myRsvp?.status === "presente" ? "border-bayside bg-bayside/10 text-bayside" : "border-zinc-800 text-zinc-400 hover:border-bayside"} ${isExpired ? "opacity-40 cursor-not-allowed" : ""}`}
                        data-testid={`list-rsvp-presente-${assembly.id}`}
                    >
                        <CheckCircle2 className="h-4 w-4 inline mr-1.5" /> Sarò presente
                    </button>
                    <button
                        onClick={(e) => quickRsvp("delegato", e)}
                        disabled={saving || isExpired}
                        className={`px-4 py-2 rounded-full text-sm border-2 transition ${myRsvp?.status === "delegato" ? "border-magenta bg-magenta/10 text-magenta" : "border-zinc-800 text-zinc-400 hover:border-magenta"} ${isExpired ? "opacity-40 cursor-not-allowed" : ""}`}
                        data-testid={`list-rsvp-delegato-${assembly.id}`}
                    >
                        <UserCheck className="h-4 w-4 inline mr-1.5" /> {myRsvp?.status === "delegato" ? `Delegato: ${myRsvp.delegate_display || "—"}` : "Delega"}
                    </button>
                    <button
                        onClick={(e) => quickRsvp("assente", e)}
                        disabled={saving || isExpired}
                        className={`px-4 py-2 rounded-full text-sm border-2 transition ${myRsvp?.status === "assente" ? "border-vermillion bg-vermillion/10 text-vermillion" : "border-zinc-800 text-zinc-400 hover:border-vermillion"} ${isExpired ? "opacity-40 cursor-not-allowed" : ""}`}
                        data-testid={`list-rsvp-assente-${assembly.id}`}
                    >
                        <XCircle className="h-4 w-4 inline mr-1.5" /> Sarò assente
                    </button>
                </div>
                {myRsvp?.updated_at && <p className="text-[10px] text-zinc-600 mt-2 font-grotesk">Ultima modifica: {fmtDate(myRsvp.updated_at)}</p>}
            </div>
        </div>
    );
}

function AssemblyDetail({ id }) {
    const navigate = useNavigate();
    const [assembly, setAssembly] = useState(null);
    const [myRsvp, setMyRsvp] = useState(null);
    const [delegatables, setDelegatables] = useState([]);
    const [choice, setChoice] = useState(null);
    const [delegateId, setDelegateId] = useState("");
    const [note, setNote] = useState("");
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [a, r, d] = await Promise.all([
                apiPrivate.get(`/assemblies/${id}`),
                apiPrivate.get(`/assemblies/${id}/my-rsvp`),
                apiPrivate.get(`/assemblies/${id}/delegatable-users`).catch(() => ({ data: [] })),
            ]);
            setAssembly(a.data);
            setMyRsvp(r.data);
            setDelegatables(d.data || []);
            if (r.data) {
                setChoice(r.data.status);
                setDelegateId(r.data.delegate_user_id || "");
                setNote(r.data.note || "");
            }
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, [id]);

    const submit = async () => {
        if (!choice) { toast.error("Scegli un'opzione"); return; }
        if (choice === "delegato" && !delegateId) { toast.error("Seleziona un socio a cui delegare"); return; }
        setSaving(true);
        try {
            const r = await apiPrivate.put(`/assemblies/${id}/rsvp`, {
                status: choice,
                delegate_user_id: choice === "delegato" ? delegateId : null,
                note: note || null,
            });
            setMyRsvp(r.data);
            toast.success("Risposta salvata");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore salvataggio");
        } finally {
            setSaving(false);
        }
    };

    const downloadIcs = async () => {
        try {
            const r = await apiPrivate.get(`/assemblies/${id}/ics`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const link = document.createElement("a");
            link.href = url;
            link.download = `assemblea-${id.slice(0, 8)}.ics`;
            link.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Errore download .ics");
        }
    };

    if (loading) return <AppLayout><div className="p-12 text-center"><Loader2 className="h-6 w-6 animate-spin inline text-zinc-500" /></div></AppLayout>;
    if (!assembly) return <AppLayout><div className="p-12 text-center text-zinc-500">Assemblea non trovata.</div></AppLayout>;

    const isClosed = assembly.status === "closed";
    const deadlineIso = assembly.rsvp_deadline || assembly.date_time;
    const deadlineDate = deadlineIso ? new Date(deadlineIso) : null;
    const isExpired = !isClosed && deadlineDate ? Date.now() > deadlineDate.getTime() : false;
    const isCustomDeadline = !!assembly.rsvp_deadline;

    return (
        <AppLayout>
            <div className="max-w-4xl mx-auto" data-testid="assembly-detail">
                <button onClick={() => navigate("/app/assemblee")} className="text-zinc-500 hover:text-white text-sm flex items-center gap-1 mb-4">
                    <ArrowLeft className="h-4 w-4" /> Torna alle assemblee
                </button>

                <div className="bento p-8">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${isClosed ? "bg-zinc-700/40 text-zinc-400" : "bg-bayside/15 text-bayside"}`}>
                            {isClosed ? "Chiusa" : "Aperta"}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-xs bg-magenta/15 text-magenta">{assembly.type}</span>
                    </div>
                    <h1 className="font-display text-4xl mt-3 tracking-tighter">{assembly.title}</h1>
                    {assembly.description && <p className="text-zinc-400 mt-3">{assembly.description}</p>}

                    <div className="grid md:grid-cols-2 gap-4 mt-6">
                        <div className="rounded-2xl bg-zinc-900/40 p-4">
                            <div className="text-xs text-zinc-500 uppercase tracking-widest">Data e ora</div>
                            <div className="text-sm mt-1">{fmtDate(assembly.date_time)}</div>
                            <div className="text-xs text-zinc-500 mt-1">Durata: {assembly.duration_minutes} min · {assembly.mode}</div>
                        </div>
                        <div className="rounded-2xl bg-zinc-900/40 p-4">
                            <div className="text-xs text-zinc-500 uppercase tracking-widest">Dove</div>
                            {assembly.location && <div className="text-sm mt-1 flex items-center gap-2"><MapPin className="h-4 w-4 text-bayside" /> {assembly.location}</div>}
                            {assembly.videocall_url && (
                                <div className="text-sm mt-2 flex items-center gap-2">
                                    <Video className="h-4 w-4 text-magenta" />
                                    <a href={assembly.videocall_url} target="_blank" rel="noopener noreferrer" className="text-magenta hover:underline truncate">{assembly.videocall_url}</a>
                                </div>
                            )}
                            <button onClick={downloadIcs} className="btn-ghost text-xs mt-3" data-testid="download-ics-btn">
                                <Download className="h-3 w-3" /> Scarica .ics
                            </button>
                        </div>
                    </div>

                    {assembly.agenda?.length > 0 && (
                        <div className="mt-6">
                            <div className="text-xs text-zinc-500 uppercase tracking-widest mb-3">Ordine del giorno</div>
                            <ol className="space-y-2">
                                {assembly.agenda.map((a, i) => (
                                    <li key={a.id} className="flex gap-3 p-3 rounded-xl bg-zinc-900/40">
                                        <span className="text-magenta font-display text-xl">{i + 1}</span>
                                        <div>
                                            <div className="font-semibold text-sm">{a.title}</div>
                                            {a.description && <div className="text-xs text-zinc-500 mt-1">{a.description}</div>}
                                        </div>
                                    </li>
                                ))}
                            </ol>
                        </div>
                    )}
                </div>

                {/* RSVP block */}
                <div className="bento p-8 mt-6">
                    <div className="flex items-start justify-between flex-wrap gap-2">
                        <h2 className="text-2xl font-display tracking-tighter">La tua risposta</h2>
                        {deadlineDate && !isClosed && (
                            <div
                                className={`text-xs font-grotesk flex items-center gap-1.5 ${isExpired ? "text-vermillion" : "text-zinc-500"}`}
                                data-testid="detail-rsvp-deadline"
                            >
                                <Clock className="h-3.5 w-3.5" />
                                {isExpired
                                    ? "Termine scaduto"
                                    : (isCustomDeadline
                                        ? <>Rispondi entro <span className="text-white ml-1">{fmtDate(deadlineIso)}</span></>
                                        : <>Rispondi entro il giorno dell'assemblea</>
                                    )}
                            </div>
                        )}
                    </div>
                    {isClosed ? (
                        <div className="mt-4 p-4 rounded-xl bg-zinc-900/40 border border-zinc-800">
                            <p className="text-sm text-zinc-400">Assemblea chiusa. La tua risposta finale:</p>
                            <p className="text-lg font-semibold mt-2">
                                {myRsvp ? (
                                    myRsvp.status === "delegato"
                                        ? <>Delegato a <span className="text-magenta">{myRsvp.delegate_display}</span></>
                                        : <span className={myRsvp.status === "presente" ? "text-bayside" : "text-vermillion"}>{myRsvp.status === "presente" ? "Presente" : "Assente"}</span>
                                ) : <span className="text-zinc-500">Non hai risposto</span>}
                            </p>
                        </div>
                    ) : isExpired ? (
                        <div className="mt-4 p-4 rounded-xl bg-vermillion/10 border border-vermillion/30" data-testid="rsvp-expired-banner">
                            <p className="text-sm text-vermillion">Il termine per rispondere è scaduto{isCustomDeadline ? ` il ${fmtDate(deadlineIso)}` : ""}.</p>
                            {myRsvp ? (
                                <p className="text-lg font-semibold mt-2">
                                    Ultima risposta salvata: {myRsvp.status === "delegato"
                                        ? <>Delegato a <span className="text-magenta">{myRsvp.delegate_display}</span></>
                                        : <span className={myRsvp.status === "presente" ? "text-bayside" : "text-vermillion"}>{myRsvp.status === "presente" ? "Presente" : "Assente"}</span>
                                    }
                                </p>
                            ) : <p className="text-sm text-zinc-400 mt-2">Non hai risposto in tempo.</p>}
                        </div>
                    ) : (
                        <>
                            <p className="text-sm text-zinc-500 mt-1">Puoi modificare la tua scelta fino alla chiusura dell'assemblea.</p>
                            <div className="grid sm:grid-cols-3 gap-3 mt-4">
                                <button
                                    onClick={() => setChoice("presente")}
                                    className={`p-4 rounded-2xl border-2 transition text-left ${choice === "presente" ? "border-bayside bg-bayside/5" : "border-zinc-800 hover:border-zinc-700"}`}
                                    data-testid="choice-presente"
                                >
                                    <CheckCircle2 className={`h-6 w-6 mb-2 ${choice === "presente" ? "text-bayside" : "text-zinc-500"}`} />
                                    <div className="font-semibold">Sarò presente</div>
                                    <div className="text-xs text-zinc-500 mt-1">Confermo la mia partecipazione.</div>
                                </button>
                                <button
                                    onClick={() => setChoice("delegato")}
                                    className={`p-4 rounded-2xl border-2 transition text-left ${choice === "delegato" ? "border-magenta bg-magenta/5" : "border-zinc-800 hover:border-zinc-700"}`}
                                    data-testid="choice-delegato"
                                >
                                    <UserCheck className={`h-6 w-6 mb-2 ${choice === "delegato" ? "text-magenta" : "text-zinc-500"}`} />
                                    <div className="font-semibold">Delega un altro socio</div>
                                    <div className="text-xs text-zinc-500 mt-1">Affida la tua voce a un altro socio.</div>
                                </button>
                                <button
                                    onClick={() => setChoice("assente")}
                                    className={`p-4 rounded-2xl border-2 transition text-left ${choice === "assente" ? "border-vermillion bg-vermillion/5" : "border-zinc-800 hover:border-zinc-700"}`}
                                    data-testid="choice-assente"
                                >
                                    <XCircle className={`h-6 w-6 mb-2 ${choice === "assente" ? "text-vermillion" : "text-zinc-500"}`} />
                                    <div className="font-semibold">Sarò assente</div>
                                    <div className="text-xs text-zinc-500 mt-1">Non potrò partecipare.</div>
                                </button>
                            </div>

                            {choice === "delegato" && (
                                <div className="mt-4" data-testid="delegate-block">
                                    <label className="text-xs text-zinc-500 uppercase tracking-widest">Socio delegato</label>
                                    <select value={delegateId} onChange={e => setDelegateId(e.target.value)} className="input-field" data-testid="delegate-select">
                                        <option value="">— Seleziona un socio —</option>
                                        {delegatables.map(u => (
                                            <option key={u.id} value={u.id}>{u.display_name} {u.quota_categories.length ? `(Quote ${u.quota_categories.join(", ")})` : ""}</option>
                                        ))}
                                    </select>
                                    {delegatables.length === 0 && <p className="text-xs text-vermillion mt-2">Nessun socio disponibile per la delega in questo momento.</p>}
                                </div>
                            )}

                            <div className="mt-4">
                                <label className="text-xs text-zinc-500 uppercase tracking-widest">Note (opzionale)</label>
                                <textarea value={note} onChange={e => setNote(e.target.value)} rows={2} className="input-field" placeholder="Eventuali comunicazioni per la segreteria" data-testid="rsvp-note" />
                            </div>

                            <div className="flex justify-end mt-4">
                                <button onClick={submit} disabled={saving || !choice} className="btn-rebel" data-testid="save-rsvp-btn">
                                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                                    Salva risposta
                                </button>
                            </div>

                            {myRsvp && (
                                <p className="text-xs text-zinc-500 mt-3">
                                    Ultima modifica: {fmtDate(myRsvp.updated_at)}
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </AppLayout>
    );
}
